import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, Palette } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Slider } from '../../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { ImageField } from './AdminCrud';
import { api, fileUrl } from '../../lib/api';
import { toast } from 'sonner';

// Campaign Entrance panel: controls the minimal gateway page at "/".
// Steve swaps the portrait and tagline here with zero code changes.
const EntrancePanel = () => {
  const [cfg, setCfg] = useState({ portrait_url: '', tagline: '', secondary_line: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/admin/entrance-page').then((r) => setCfg((c) => ({ ...c, ...r.data }))).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/entrance-page', cfg);
      setCfg((c) => ({ ...c, ...r.data }));
      toast.success('Entrance page updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card className="mb-5" data-testid="admin-entrance-panel">
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="font-serif font-black text-lg text-[#1a2744]">Campaign Entrance Page ( / )</p>
            <p className="text-sm text-slate-500">The minimal gateway visitors see first. Portrait and tagline are swappable here. The full homepage lives at /home.</p>
          </div>
          <Button asChild variant="outline" className="h-10 font-bold btn-press" data-testid="admin-entrance-preview-link">
            <a href="/" target="_blank" rel="noopener noreferrer">View Entrance</a>
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Steve Photo (used as the full entrance background)</p>
            <ImageField value={cfg.portrait_url} onChange={(v) => setCfg((c) => ({ ...c, portrait_url: v }))} category="entrance" accept="image/*" />
            <p className="text-xs text-slate-500 mt-1.5">Upload a new photo any time. A wide photo with Steve on the right works best; the entrance adds a soft white fade on the left so the logo stays readable.</p>
          </div>
          <div className="space-y-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Tagline</p>
              <Input value={cfg.tagline} onChange={(e) => setCfg((c) => ({ ...c, tagline: e.target.value }))} placeholder="ACCOUNTABILITY YOU CAN SEE." className="h-11" data-testid="admin-entrance-tagline-input" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Optional small second line (leave blank to keep it minimal)</p>
              <Input value={cfg.secondary_line} onChange={(e) => setCfg((c) => ({ ...c, secondary_line: e.target.value }))} placeholder="HOPE WITH ACTION." className="h-11" data-testid="admin-entrance-secondary-input" />
            </div>
            <Button onClick={save} disabled={saving} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="admin-entrance-save-button">
              {saving ? 'Saving...' : 'Save Entrance Page'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EMPTY_SLIDE = {
  title: '', subtitle: '', description: '', media_type: 'image', image_url: '', video_url: '', embed_url: '',
  ctas: [], text_align: 'left', text_color: '#FFFFFF', active: true, publish_date: '',
  variant: 'standard', logo_url: '', date_text: '', secondary_ctas: [],
  kicker: '', body: '', chips: '', icon_url: '', value_heading: '', value_text: '', caution_text: '', callout: '', strip_text: '', note: '',
  focus_desktop: 'right center', focus_mobile: '76% center', gradient_strength: 85, logo_size: 'lg',
  overlay: { type: 'blend_left', color: '#1a2744', opacity: 75, text_shadow: true, blur: false, focus_position: 'center' },
};

export default function AdminHero() {
  const [slides, setSlides] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SLIDE);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => api.get('/admin/hero').then((r) => setSlides(r.data || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setOverlay = (k, v) => setForm((f) => ({ ...f, overlay: { ...f.overlay, [k]: v } }));
  const setCta = (i, k, v) => setForm((f) => ({ ...f, ctas: f.ctas.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));
  const setSecondary = (i, k, v) => setForm((f) => ({ ...f, secondary_ctas: (f.secondary_ctas || []).map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));
  const isLight = form.variant === 'light_logo';
  const isTrackerPromo = form.variant === 'tracker_promo';
  const isElection = form.variant === 'election_promo';

  const save = async () => {
    setBusy(true);
    try {
      if (editing?.id) await api.put(`/hero/${editing.id}`, form);
      else await api.post('/hero', { ...form, order: slides.length });
      toast.success('Slide saved');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/hero/${id}`);
    toast.success('Slide deleted');
    load();
  };

  const move = async (i, dir) => {
    const copy = [...slides];
    const j = i + dir;
    if (j < 0 || j >= copy.length) return;
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setSlides(copy);
    await api.post('/hero/reorder', { ids: copy.map((s) => s.id) });
  };

  return (
    <div data-testid="admin-hero">
      <EntrancePanel />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="font-serif font-black text-2xl text-[#1a2744]">Hero Manager</h1>
          <p className="text-sm text-slate-500">Homepage carousel slides with full overlay controls.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="hero-designs-link"><Link to="/admin/designs"><Palette className="h-4 w-4 mr-1.5" /> Design Manager</Link></Button>
          <Button onClick={() => { setForm(EMPTY_SLIDE); setEditing({}); }} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="hero-add-button"><Plus className="h-4 w-4 mr-1.5" /> Add Slide</Button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="space-y-3">
          {slides.map((s, i) => (
            <Card key={s.id} data-testid="hero-slide-row">
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="h-20 w-full sm:w-36 shrink-0 rounded-lg overflow-hidden bg-[#1a2744]">
                  {s.image_url && <img src={fileUrl(s.image_url)} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-serif font-bold text-[#1a2744] truncate">{s.title || '(untitled slide)'}</p>
                    {s.active ? <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">Active</Badge> : <Badge variant="outline" className="bg-slate-100 text-slate-500">Inactive</Badge>}
                  </div>
                  <p className="text-sm text-slate-500 truncate">{s.subtitle}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Overlay: {s.overlay?.type} · {s.overlay?.opacity}% · {s.media_type}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => move(i, -1)} className="p-2 rounded-lg hover:bg-slate-100" aria-label="Move up" data-testid="hero-move-up"><ArrowUp className="h-4 w-4" /></button>
                  <button onClick={() => move(i, 1)} className="p-2 rounded-lg hover:bg-slate-100" aria-label="Move down" data-testid="hero-move-down"><ArrowDown className="h-4 w-4" /></button>
                  <button onClick={() => { setForm({ ...EMPTY_SLIDE, ...s, overlay: { ...EMPTY_SLIDE.overlay, ...(s.overlay || {}) } }); setEditing(s); }} className="p-2 rounded-lg hover:bg-[#EAF2FF] text-[#1a2744]" aria-label="Edit" data-testid="hero-edit-button"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(s.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" aria-label="Delete" data-testid="hero-delete-button"><Trash2 className="h-4 w-4" /></button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="hero-edit-dialog">
          <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">{editing?.id ? 'Edit Slide' : 'New Slide'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Slide style</label>
              <Select value={form.variant || 'standard'} onValueChange={(v) => set('variant', v)}>
                <SelectTrigger className="h-11" data-testid="hero-variant-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard (photo background, light text)</SelectItem>
                  <SelectItem value="light_logo">Full background light logo hero (bright, navy text, logo + date line)</SelectItem>
                  <SelectItem value="tracker_promo">Issue Tracker promo (dark navy civic slide — logo, chips, value card)</SelectItem>
                  <SelectItem value="election_promo">Election Centre promo (navy civic grid — eyebrow, note box, buttons)</SelectItem>
                </SelectContent>
              </Select>
              {isLight && <p className="text-xs text-slate-500 mt-1.5">Full-width background photo (Steve right-adjusted) with a light readability gradient, logo, date pill, copy and buttons on the left. Dark overlay controls are not used for this style.</p>}
              {isTrackerPromo && <p className="text-xs text-slate-500 mt-1.5">Power-punch campaign-tech slide for the Ward 8 Public Issue Tracker: navy map-grid background (or optional photo), logo showcase, punch line, feature chips, value card and scale strip. Subtitle = tagline, Description = punch line.</p>}
              {isElection && <p className="text-xs text-slate-500 mt-1.5">Election Centre promo: navy civic grid with gold accents. Kicker = eyebrow, Title = headline, Subtitle = subheadline, Description = body copy (hidden on small phones), plus the note box below. Button links that start with http open in a new tab.</p>}
            </div>
            <div><label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Title {isLight && <span className="font-normal text-slate-500">(used for accessibility/alt text — the logo is shown visually)</span>}</label><Input value={form.title} onChange={(e) => set('title', e.target.value)} className="h-11" data-testid="hero-title-input" /></div>
            <div><label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Subtitle</label><Input value={form.subtitle} onChange={(e) => set('subtitle', e.target.value)} className="h-11" /></div>
            <div><label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Description {isTrackerPromo && <span className="font-normal text-slate-500">(punch line — e.g. "Steve promises accountability by building the system for it.")</span>}{isElection && <span className="font-normal text-slate-500">(body copy, hidden on small phones)</span>}</label><Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={3} /></div>

            {isElection && (
              <div className="bg-[#f0f3fa] border border-[#1a2744]/15 rounded-xl p-4 space-y-4" data-testid="election-promo-fields">
                <p className="font-bold text-[#1a2744] text-sm">Election Centre promo content</p>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Eyebrow (small gold line above the headline)</label><Input value={form.kicker || ''} onChange={(e) => set('kicker', e.target.value)} className="h-10 bg-white" placeholder="2026 Municipal Election" data-testid="election-kicker-input" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Note (confirm-with-the-City reminder box)</label><Textarea value={form.note || ''} onChange={(e) => set('note', e.target.value)} rows={2} className="bg-white" placeholder="Information can change. Always confirm final election details through the official City of Kawartha Lakes election pages." data-testid="election-note-input" /></div>
              </div>
            )}

            {isTrackerPromo && (
              <div className="bg-[#f0f3fa] border border-[#1a2744]/15 rounded-xl p-4 space-y-4" data-testid="tracker-promo-fields">
                <p className="font-bold text-[#1a2744] text-sm">Issue Tracker promo content</p>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Kicker pill (small line above title)</label><Input value={form.kicker || ''} onChange={(e) => set('kicker', e.target.value)} className="h-10 bg-white" data-testid="tracker-kicker-input" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Supporting body text (blank line between paragraphs — only the first paragraph shows on phones)</label><Textarea value={form.body || ''} onChange={(e) => set('body', e.target.value)} rows={5} className="bg-white" data-testid="tracker-body-input" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Feature chips (one per line)</label><Textarea value={form.chips || ''} onChange={(e) => set('chips', e.target.value)} rows={4} className="bg-white" data-testid="tracker-chips-input" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Main logo (dark tile — shown on its own, kept clean)</label><ImageField value={form.logo_url} onChange={(v) => set('logo_url', v)} category="logos" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Value heading</label><Input value={form.value_heading || ''} onChange={(e) => set('value_heading', e.target.value)} className="h-10 bg-white" data-testid="tracker-value-heading-input" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Value / contrast text</label><Textarea value={form.value_text || ''} onChange={(e) => set('value_text', e.target.value)} rows={3} className="bg-white" data-testid="tracker-value-text-input" /></div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Cautionary value/comparison text (appears publicly — keep careful, never sensational)</label>
                  <Textarea value={form.caution_text || ''} onChange={(e) => set('caution_text', e.target.value)} rows={3} className="bg-white" data-testid="tracker-caution-input" />
                  <p className="text-[11px] text-slate-500 mt-1">Use only as a cautionary example (e.g. ArriveCAN) of why taxpayers want affordable, accountable digital tools. Never imply the Issue Tracker is the same as any other project. Clear this field to remove the line entirely.</p>
                </div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Callout line</label><Input value={form.callout || ''} onChange={(e) => set('callout', e.target.value)} className="h-10 bg-white" data-testid="tracker-callout-input" /></div>
                <div><label className="block text-xs font-bold text-slate-600 mb-1">Scale strip (bottom line)</label><Input value={form.strip_text || ''} onChange={(e) => set('strip_text', e.target.value)} className="h-10 bg-white" data-testid="tracker-strip-input" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Photo focal point — desktop</label>
                    <Select value={form.focus_desktop || 'center'} onValueChange={(v) => set('focus_desktop', v)}>
                      <SelectTrigger className="h-10 bg-white" data-testid="tracker-focus-desktop"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="center">Centre</SelectItem>
                        <SelectItem value="center top">Top</SelectItem>
                        <SelectItem value="center bottom">Bottom</SelectItem>
                        <SelectItem value="left center">Left</SelectItem>
                        <SelectItem value="right center">Right</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Photo focal point — mobile</label>
                    <Select value={form.focus_mobile || 'center'} onValueChange={(v) => set('focus_mobile', v)}>
                      <SelectTrigger className="h-10 bg-white" data-testid="tracker-focus-mobile"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="center">Centre</SelectItem>
                        <SelectItem value="center top">Top</SelectItem>
                        <SelectItem value="center bottom">Bottom</SelectItem>
                        <SelectItem value="left center">Left</SelectItem>
                        <SelectItem value="right center">Right</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-[11px] text-slate-500">Background photo (e.g. the aerial Ward 8 shot) is set below. When a photo is used, the <strong>Overlay colour + transparency</strong> controls below become the navy wash on top of it — raise the transparency % for a stronger wash and easier-to-read text. Leave the photo empty for the built-in navy map-grid pattern. Buttons and text links are edited in the sections below.</p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Background type</label>
                <Select value={form.media_type} onValueChange={(v) => set('media_type', v)}>
                  <SelectTrigger className="h-11" data-testid="hero-media-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Photo background</SelectItem>
                    <SelectItem value="video">Video background (uploaded)</SelectItem>
                    <SelectItem value="embed">YouTube/Rumble/Vimeo embed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Text alignment</label>
                <Select value={form.text_align} onValueChange={(v) => set('text_align', v)}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="center">Center</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {isLight && (
              <>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Election date line (HTML text — editable)</label>
                  <Input value={form.date_text || ''} onChange={(e) => set('date_text', e.target.value)} placeholder="Voting: Fri, Oct 16 – Mon, Oct 26, 2026" className="h-11" data-testid="hero-date-text-input" />
                  <p className="text-xs text-slate-500 mt-1">Shown as a small civic line above the logo. Never bake dates into the logo image.</p>
                </div>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Campaign logo (corrected — no date embedded)</label>
                  <ImageField value={form.logo_url} onChange={(v) => set('logo_url', v)} category="logos" />
                </div>
                <div className="bg-[#f6f4ee] border border-[#C9A84C]/40 rounded-xl p-4 space-y-4">
                  <p className="font-bold text-[#1a2744] text-sm">Full-background layout controls</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 mb-1">Background focal point — desktop</label>
                      <Select value={form.focus_desktop || 'right center'} onValueChange={(v) => set('focus_desktop', v)}>
                        <SelectTrigger className="h-10 bg-white" data-testid="hero-focus-desktop"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="right center">Right (Steve fully right)</SelectItem>
                          <SelectItem value="72% center">Right of centre</SelectItem>
                          <SelectItem value="center">Centre</SelectItem>
                          <SelectItem value="left center">Left</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-600 mb-1">Background focal point — mobile</label>
                      <Select value={form.focus_mobile || '76% center'} onValueChange={(v) => set('focus_mobile', v)}>
                        <SelectTrigger className="h-10 bg-white" data-testid="hero-focus-mobile"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="right center">Right</SelectItem>
                          <SelectItem value="76% center">Right of centre (keeps face visible)</SelectItem>
                          <SelectItem value="65% center">Slightly right</SelectItem>
                          <SelectItem value="center">Centre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-2">Light gradient strength (text readability): {typeof form.gradient_strength === 'number' ? form.gradient_strength : 85}%</label>
                    <Slider value={[typeof form.gradient_strength === 'number' ? form.gradient_strength : 85]} onValueChange={([v]) => set('gradient_strength', v)} min={0} max={100} step={5} data-testid="hero-gradient-strength" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Logo size</label>
                    <Select value={form.logo_size || 'lg'} onValueChange={(v) => set('logo_size', v)}>
                      <SelectTrigger className="h-10 bg-white" data-testid="hero-logo-size"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sm">Small</SelectItem>
                        <SelectItem value="md">Medium</SelectItem>
                        <SelectItem value="lg">Large (recommended)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {form.media_type === 'image' && (
              <div><label className="block font-bold text-[#1a2744] mb-1.5 text-sm">{isLight ? 'Steve photo (full slide background — right-adjusted)' : 'Background photo'}</label><ImageField value={form.image_url} onChange={(v) => set('image_url', v)} category="hero" /></div>
            )}
            {form.media_type === 'video' && (
              <div><label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Video file (mp4/webm)</label><ImageField value={form.video_url} onChange={(v) => set('video_url', v)} category="hero" accept="video/*" /></div>
            )}
            {form.media_type === 'embed' && (
              <div><label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Embed URL</label><Input value={form.embed_url} onChange={(e) => set('embed_url', e.target.value)} placeholder="https://www.youtube.com/embed/VIDEO_ID" className="h-11" /></div>
            )}

            {/* Overlay controls */}
            {!isLight && (
            <div className="bg-[#EAF2FF] border border-[#CFE0FF] rounded-xl p-4 space-y-4">
              <p className="font-bold text-[#1a2744] text-sm">Overlay controls</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Overlay style</label>
                  <Select value={form.overlay.type} onValueChange={(v) => setOverlay('type', v)}>
                    <SelectTrigger className="h-10 bg-white" data-testid="hero-overlay-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="blend_left">Black blend, left → right</SelectItem>
                      <SelectItem value="blend_right">Black blend, right → left</SelectItem>
                      <SelectItem value="solid">Solid dark overlay</SelectItem>
                      <SelectItem value="tone">Colour tone overlay</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Overlay colour</label>
                  <div className="flex gap-2">
                    <input type="color" value={form.overlay.color} onChange={(e) => setOverlay('color', e.target.value)} className="h-10 w-14 rounded border cursor-pointer" data-testid="hero-overlay-color" />
                    <Input value={form.overlay.color} onChange={(e) => setOverlay('color', e.target.value)} className="h-10 bg-white" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-2">Overlay transparency: {form.overlay.opacity}%</label>
                <Slider value={[form.overlay.opacity]} onValueChange={([v]) => setOverlay('opacity', v)} min={0} max={100} step={5} data-testid="hero-overlay-opacity" />
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Switch checked={form.overlay.text_shadow} onCheckedChange={(v) => setOverlay('text_shadow', v)} /> Text shadow</label>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Switch checked={form.overlay.blur} onCheckedChange={(v) => setOverlay('blur', v)} /> Background blur</label>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Mobile crop focus</label>
                <Select value={form.overlay.focus_position} onValueChange={(v) => setOverlay('focus_position', v)}>
                  <SelectTrigger className="h-10 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['center', 'top', 'bottom', 'left', 'right'].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            )}

            {/* Text color */}
            <div>
              <label className="block font-bold text-[#1a2744] mb-1.5 text-sm">Text colour {isLight && <span className="font-normal text-slate-500">(slogan line — keep dark navy for the light hero)</span>}</label>
              <div className="flex gap-2">
                <input type="color" value={form.text_color} onChange={(e) => set('text_color', e.target.value)} className="h-10 w-14 rounded border cursor-pointer" />
                <Input value={form.text_color} onChange={(e) => set('text_color', e.target.value)} className="h-10" />
              </div>
            </div>

            {/* CTAs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="font-bold text-[#1a2744] text-sm">CTA buttons</label>
                <Button type="button" variant="outline" size="sm" className="font-bold" onClick={() => set('ctas', [...form.ctas, { label: '', url: '', style: 'gold' }])} data-testid="hero-add-cta"><Plus className="h-3.5 w-3.5 mr-1" /> Add button</Button>
              </div>
              <div className="space-y-2">
                {form.ctas.map((c, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={c.label} onChange={(e) => setCta(i, 'label', e.target.value)} placeholder="Label" className="h-10" />
                    <Input value={c.url} onChange={(e) => setCta(i, 'url', e.target.value)} placeholder="/volunteer" className="h-10" />
                    <Select value={c.style} onValueChange={(v) => setCta(i, 'style', v)}>
                      <SelectTrigger className="h-10 w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gold">Gold</SelectItem>
                        <SelectItem value="navy">Navy</SelectItem>
                        <SelectItem value="outline">Outline</SelectItem>
                      </SelectContent>
                    </Select>
                    <button onClick={() => set('ctas', form.ctas.filter((_, j) => j !== i))} className="text-red-600 p-2" aria-label="Remove CTA"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            {(isLight || isTrackerPromo) && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="font-bold text-[#1a2744] text-sm">Secondary links (small text links under buttons)</label>
                  <Button type="button" variant="outline" size="sm" className="font-bold" onClick={() => set('secondary_ctas', [...(form.secondary_ctas || []), { label: '', url: '' }])} data-testid="hero-add-secondary"><Plus className="h-3.5 w-3.5 mr-1" /> Add link</Button>
                </div>
                <div className="space-y-2">
                  {(form.secondary_ctas || []).map((c, i) => (
                    <div key={i} className="flex gap-2">
                      <Input value={c.label} onChange={(e) => setSecondary(i, 'label', e.target.value)} placeholder="About Steve" className="h-10" />
                      <Input value={c.url} onChange={(e) => setSecondary(i, 'url', e.target.value)} placeholder="/meet-steve" className="h-10" />
                      <button onClick={() => set('secondary_ctas', (form.secondary_ctas || []).filter((_, j) => j !== i))} className="text-red-600 p-2" aria-label="Remove link"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Switch checked={form.active} onCheckedChange={(v) => set('active', v)} data-testid="hero-active-switch" /> Slide active</label>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Scheduled publish date (optional)</label>
                <Input type="date" value={form.publish_date || ''} onChange={(e) => set('publish_date', e.target.value)} className="h-10" />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={save} disabled={busy} className="flex-1 h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="hero-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Save Slide</Button>
              <Button variant="outline" onClick={() => setEditing(null)} className="h-11 font-bold">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
